import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { VideoCameraIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import UpgradedLoader from '../UpgradedLoader';
import Cookies from 'universal-cookie';
import axios from 'axios';

const ChunkedUploader = forwardRef(({
    file,
    fileName,
    metadata,
    onProgress,
    onComplete,
    onError,
    projectUuid,
    teamUuid
}, ref) => {
    const { t } = useTranslation();
    const [uploadState, setUploadState] = useState({
        sessionId: null,
        status: 'idle', // idle, starting, uploading, assembling, completed, error
        totalChunks: 0,
        completedChunks: 0,
        currentChunk: 0,
        progressPercentage: 0,
        uploadSpeed: 0,
        estimatedTimeRemaining: 0,
        errorMessage: null
    });

    const [startTime, setStartTime] = useState(null);
    const [uploadedBytes, setUploadedBytes] = useState(0);
    const abortControllerRef = useRef(null);

    const CHUNK_SIZE = 50 * 1024 * 1024; // 50MB chunks (matches backend validation limit)

    // Create file chunks
    const createChunks = useCallback((file) => {
        const chunks = [];
        let start = 0;
        let index = 0;
        
        while (start < file.size) {
            const end = Math.min(start + CHUNK_SIZE, file.size);
            const chunk = file.slice(start, end);
            chunks.push({
                index,
                data: chunk,
                start,
                end
            });
            start = end;
            index++;
        }
        
        return chunks;
    }, []);

    // Calculate upload statistics
    const updateUploadStats = useCallback((completedChunks, totalChunks) => {
        if (!startTime) return;
        
        const currentTime = Date.now();
        const elapsedTime = (currentTime - startTime) / 1000; // seconds
        const newUploadedBytes = completedChunks * CHUNK_SIZE;
        const speed = newUploadedBytes / elapsedTime; // bytes per second
        
        const remainingBytes = (totalChunks - completedChunks) * CHUNK_SIZE;
        const estimatedTimeRemaining = speed > 0 ? remainingBytes / speed : 0;
        
        setUploadedBytes(newUploadedBytes);
        setUploadState(prev => ({
            ...prev,
            uploadSpeed: speed,
            estimatedTimeRemaining
        }));
    }, [startTime]);

    // Format file size for display
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    // Format time for display
    const formatTime = (seconds) => {
        if (!seconds || seconds === Infinity) return '--:--';
        const minutes = Math.floor(seconds / 60);
        const secs = Math.floor(seconds % 60);
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
    };

    // Format speed for display
    const formatSpeed = (bytesPerSecond) => {
        if (!bytesPerSecond) return '-- MB/s';
        return `${formatFileSize(bytesPerSecond)}/s`;
    };

    // Start chunked upload
    const startUpload = async () => {
        if (!file || !fileName || !metadata || !projectUuid || !teamUuid) {
            onError('Missing required upload parameters');
            return;
        }

        // Validate authentication token before starting
        const token = getToken();
        if (!token) {
            handleAuthFailure('No authentication token found');
            return;
        }

        // Starting chunked upload with validated token

        setUploadState(prev => ({ ...prev, status: 'starting' }));
        setStartTime(Date.now());

        try {
            // Start upload session
            const startUrl = `${process.env.REACT_APP_API_BASE_URL}/video_analysis/chunked/start`;
            const token = getToken();
            
            // Validate required parameters
            if (!token) {
                throw new Error('Authentication token is missing');
            }
            if (!teamUuid) {
                throw new Error('Team UUID is missing');
            }
            if (!projectUuid) {
                throw new Error('Project UUID is missing');
            }
            if (!fileName && !file.name) {
                throw new Error('File name is missing');
            }
            
            const requestPayload = {
                file_name: fileName || file.name, // Backend expects 'file_name' not 'name'
                file_size: Number(file.size), // Ensure it's a number
                team_uuid: teamUuid,
                project_uuid: projectUuid,
                metadata: {
                    name: metadata.name || fileName || file.name,
                    language: metadata.language || 'pt',
                    number_of_speakers: metadata.number_of_speakers ? Number(metadata.number_of_speakers) : null
                }
            };
            
            // Making start request with validated payload and token
            
            const startResponse = await axios.post(startUrl, requestPayload, {
                headers: {
                    'authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            // Axios will throw for non-2xx status codes, so we handle this in catch block

            // Check content type before parsing JSON
            const contentType = startResponse.headers['content-type'];
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Non-JSON response when starting upload session:', {
                    contentType,
                    responseStart: JSON.stringify(startResponse.data).substring(0, 100),
                    status: startResponse.status
                });
                throw new Error(`Server returned non-JSON response when starting upload session (${contentType}). This usually indicates a server error or authentication redirect.`);
            }

            const { upload_id, total_chunks } = startResponse.data;
            
            setUploadState(prev => ({
                ...prev,
                sessionId: upload_id,
                totalChunks: total_chunks,
                status: 'uploading'
            }));

            // Start uploading chunks
            await uploadChunks(upload_id, total_chunks);

        } catch (error) {
            console.error('❌ Upload failed:', error);
            
            // Handle cancellation - don't show error for intentional cancellation
            if (error.message === 'Upload cancelled' || error.name === 'AbortError' || error.message === 'canceled') {
                setUploadState(prev => ({
                    ...prev,
                    status: 'cancelled',
                    errorMessage: 'Upload cancelled by user'
                }));
                return;
            }
            
            // Handle axios errors specifically
            if (error.response) {
                // Server responded with error status
                const status = error.response.status;
                const responseData = error.response.data;
                
                console.error('🔍 Server Error Details:', {
                    status,
                    statusText: error.response.statusText,
                    headers: error.response.headers,
                    data: responseData,
                    url: error.config?.url,
                    method: error.config?.method,
                    sentData: error.config?.data
                });
                
                if (status === 422) {
                    // Validation error - log detailed information
                    console.error('🚨 Validation Error (422):', {
                        message: responseData?.message || 'Validation failed',
                        errors: responseData?.errors || {},
                        sent_payload: JSON.parse(error.config?.data || '{}'),
                        endpoint: error.config?.url
                    });
                    onError(`Validation error: ${responseData?.message || 'Invalid request data'}`);
                    return;
                } else if (status === 302) {
                    console.error('302 Redirect during upload session start:', {
                        redirectLocation: error.response.headers.location,
                        requestURL: error.config?.url,
                        status: status
                    });
                    handleAuthFailure('Redirect error when starting upload session. This usually indicates authentication issues or wrong endpoint URL.');
                    return;
                } else if (status === 401 || status === 403) {
                    handleAuthFailure('Authentication error when starting upload session. Please check your login status.');
                    return;
                } else if (status === 429) {
                    onError('Rate limited when starting upload session. Server is busy.');
                    return;
                }
            } else if (error.request) {
                // Network error
                console.error('🌐 Network Error:', error.request);
                onError('Network error during upload session start');
                return;
            }
            
            // Handle specific JSON parsing errors that indicate HTML responses (redirects)
            if (error.message.includes('Unexpected token') && error.message.includes('<!DOCTYPE')) {
                handleAuthFailure('Received HTML page instead of JSON response, indicating authentication redirect');
                return;
            }
            
            setUploadState(prev => ({
                ...prev,
                status: 'error',
                errorMessage: error.message
            }));
            onError(error.message);
        }
    };

    // Upload chunks sequentially with rate limiting
    const uploadChunks = async (sessionId, totalChunks) => {
        const chunks = createChunks(file);
        abortControllerRef.current = new AbortController();
        
        // Rate limiting variables - conservative approach to prevent 429 errors
        let currentDelay = 2000; // Start with 2s delay to respect server limits
        const minDelay = 1000; // Minimum 1s delay between chunks
        const maxDelay = 10000; // Maximum 10s delay between chunks
        let consecutiveErrors = 0;

        try {
            for (let i = 0; i < chunks.length; i++) {
                if (abortControllerRef.current?.signal.aborted) {
                    throw new Error('Upload cancelled');
                }

                const chunk = chunks[i];
                
                try {
                    await uploadChunk(sessionId, chunk, totalChunks);
                    
                    // Success - reduce delay slightly for next chunk (but stay conservative)
                    consecutiveErrors = 0;
                    currentDelay = Math.max(minDelay, currentDelay * 0.95);
                    
                } catch (error) {
                    // If it's a rate limit error, increase delay significantly
                    if (error.message.includes('Rate limited')) {
                        consecutiveErrors++;
                        currentDelay = Math.min(maxDelay, currentDelay * (1.5 + consecutiveErrors * 0.5));
                        // Rate limited on chunk, increasing delay
                    }
                    throw error; // Re-throw to trigger retry logic in uploadChunk
                }
                
                // Update progress
                const completedChunks = i + 1;
                const progressPercentage = (completedChunks / totalChunks) * 100;
                
                setUploadState(prev => ({
                    ...prev,
                    currentChunk: i,
                    completedChunks,
                    progressPercentage
                }));

                updateUploadStats(completedChunks, totalChunks);
                onProgress(progressPercentage);
                
                // Add delay before next chunk (except for last chunk)
                if (i < chunks.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, currentDelay));
                }
            }

            // Complete upload
            await completeUpload(sessionId);

        } catch (error) {
            throw error;
        }
    };

    // Upload single chunk with retry logic
    const uploadChunk = async (sessionId, chunk, totalChunks, retryCount = 0) => {
        const maxRetries = 3;
        
        try {
            const formData = new FormData();
            formData.append('upload_id', sessionId);
            formData.append('chunk_index', chunk.index);
            formData.append('chunk', chunk.data);

            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/video_analysis/chunked/upload`, formData, {
                headers: {
                    'authorization': `Bearer ${getToken()}`
                },
                signal: abortControllerRef.current?.signal
            });

            // Axios automatically throws for non-2xx status codes, so no need to check response.ok

            // Check content type before parsing JSON (for axios response)
            const contentType = response.headers['content-type'];
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Non-JSON response received:', {
                    contentType,
                    responseStart: JSON.stringify(response.data).substring(0, 100),
                    status: response.status
                });
                throw new Error(`Server returned non-JSON response (${contentType}). This usually indicates a server error or authentication redirect.`);
            }

            return response.data;

        } catch (error) {
            // Handle axios errors specifically
            let errorMessage = error.message;
            if (error.response) {
                const status = error.response.status;
                if (status === 429) {
                    errorMessage = `Rate limited while uploading chunk ${chunk.index}`;
                } else if (status === 302) {
                    console.error('302 Redirect during chunk upload:', {
                        chunkIndex: chunk.index,
                        redirectLocation: error.response.headers.location,
                        requestURL: error.config?.url,
                        status: status
                    });
                    errorMessage = `Redirect error while uploading chunk ${chunk.index}. This usually indicates authentication issues or wrong endpoint URL.`;
                } else if (status === 401 || status === 403) {
                    errorMessage = `Authentication error while uploading chunk ${chunk.index}. Please check your login status.`;
                } else {
                    errorMessage = `Failed to upload chunk ${chunk.index} (${status})`;
                }
            }
            
            if (retryCount < maxRetries && !abortControllerRef.current?.signal.aborted) {
                // Longer delay for rate limiting errors with more conservative backoff
                const isRateLimit = errorMessage.includes('Rate limited');
                const baseDelay = isRateLimit ? 5000 : 2000; // 5s base for rate limits, 2s for others
                const delay = Math.pow(2, retryCount) * baseDelay; // Exponential backoff
                
                // Retrying chunk upload after delay
                await new Promise(resolve => setTimeout(resolve, delay));
                return uploadChunk(sessionId, chunk, totalChunks, retryCount + 1);
            }
            
            // If this is a rate limit error and we've exhausted retries, 
            // let the parent function know so it can adjust its strategy
            if (errorMessage.includes('Rate limited')) {
                throw new Error(`Rate limited while uploading chunk ${chunk.index} after ${maxRetries} retries`);
            }
            
            throw new Error(errorMessage);
        }
    };

    // Complete upload and assemble file
    const completeUpload = async (sessionId) => {
        setUploadState(prev => ({ ...prev, status: 'assembling' }));

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/video_analysis/chunked/complete`, {
                upload_id: sessionId
            }, {
                headers: {
                    'authorization': `Bearer ${getToken()}`
                }
            });

            // Axios automatically throws for non-2xx status codes

            // Check content type before parsing JSON
            const contentType = response.headers['content-type'];
            if (!contentType || !contentType.includes('application/json')) {
                console.error('Non-JSON response when completing upload:', {
                    contentType,
                    responseStart: JSON.stringify(response.data).substring(0, 100),
                    status: response.status
                });
                throw new Error(`Server returned non-JSON response when completing upload (${contentType}). This usually indicates a server error or authentication redirect.`);
            }

            const result = response.data;
            
            setUploadState(prev => ({ 
                ...prev, 
                status: 'completed',
                progressPercentage: 100 
            }));

            onComplete(result.analysis_uuid);

        } catch (error) {
            // Handle axios errors specifically
            if (error.response) {
                const status = error.response.status;
                if (status === 302) {
                    console.error('302 Redirect during upload completion:', {
                        redirectLocation: error.response.headers.location,
                        requestURL: error.config?.url,
                        status: status
                    });
                    throw new Error('Redirect error when completing upload. This usually indicates authentication issues or wrong endpoint URL.');
                } else if (status === 401 || status === 403) {
                    throw new Error('Authentication error when completing upload. Please check your login status.');
                } else {
                    throw new Error(`Failed to complete upload (${status})`);
                }
            }
            throw error;
        }
    };

    // Cancel upload
    const cancelUpload = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }
        
        // Call backend abort endpoint if session exists
        if (uploadState.sessionId) {
            try {
                await axios.post(`${process.env.REACT_APP_API_BASE_URL}/video_analysis/chunked/abort`, {
                    upload_id: uploadState.sessionId
                }, {
                    headers: {
                        'authorization': `Bearer ${getToken()}`
                    }
                });
            } catch (error) {
                // Failed to abort upload on server
            }
        }
        
        setUploadState(prev => ({ 
            ...prev, 
            status: 'cancelled',
            errorMessage: 'Upload cancelled by user'
        }));
        
        // Don't call onError since this is an intentional cancellation
    }, [uploadState.sessionId]);

    // Expose cancelUpload function to parent through ref
    useImperativeHandle(ref, () => ({
        cancelUpload
    }), [cancelUpload]);

    // Get auth token (same method as other requests)
    const getToken = () => {
        const cookies = new Cookies();
        const token = cookies.get("token");
        
        // Note: Laravel Sanctum tokens are not JWTs, so we skip format validation
        // Basic XSS protection only
        if (token && (token.includes('<script') || token.includes('javascript:'))) {
            return null;
        }
        
        return token;
    };

    // Handle authentication failures consistently
    const handleAuthFailure = (message) => {
        console.error('Authentication failure during chunked upload:', message);
        setUploadState(prev => ({
            ...prev,
            status: 'error',
            errorMessage: `Authentication Error: ${message}. Please log in again and retry your upload.`
        }));
        onError(`Authentication Error: ${message}. Please log in again and retry your upload.`);
    };

    // Render progress bar
    const renderProgressBar = () => (
        <div className="w-full">
            <div className="flex justify-between text-sm text-gray-600 mb-2">
                <span>{Math.round(uploadState.progressPercentage)}%</span>
                <span>{uploadState.completedChunks} / {uploadState.totalChunks} chunks</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                    className="bg-black h-3 rounded-full transition-all duration-300"
                    style={{ width: `${uploadState.progressPercentage}%` }}
                ></div>
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-2">
                <span>{formatSpeed(uploadState.uploadSpeed)}</span>
                <span>{formatTime(uploadState.estimatedTimeRemaining)} remaining</span>
            </div>
        </div>
    );

    // Auto-start upload when component mounts
    useEffect(() => {
        if (file && uploadState.status === 'idle') {
            startUpload();
        }
    }, [file]);

    return (
        <div className="w-full">
            {/* Simplified upload interface - show progress for all active states */}
            {(uploadState.status === 'idle' || uploadState.status === 'starting' || uploadState.status === 'uploading' || uploadState.status === 'assembling') && (
                <div className="py-8 text-center">
                    {/* Uploading message and progress bar */}
                    <div className="w-full max-w-md mx-auto mb-6">
                        <p className="text-lg font-medium text-gray-900 mb-4 text-center">
                            {t('VideoUpload.UploadingFile')}
                        </p>
                        <div className="relative">
                            <div className="w-full bg-gray-200 rounded-full h-4">
                                <div 
                                    className="bg-black h-4 rounded-full transition-all duration-300 animate-pulse"
                                    style={{ width: `${uploadState.progressPercentage}%` }}
                                ></div>
                            </div>
                            <div className="text-sm font-medium text-gray-700 mt-2 text-right">
                                {Math.round(uploadState.progressPercentage)}%
                            </div>
                        </div>
                    </div>

                    {/* Warning message at the end */}
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-center justify-center">
                            <svg className="w-5 h-5 text-yellow-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <p className="text-sm font-medium text-yellow-800">
                                {t('VideoUpload.DoNotLeavePageWarning')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {uploadState.status === 'completed' && (
                <div className="text-center py-8">
                    <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500 mb-4" />
                    <p className="text-lg font-medium text-green-600 mb-2">
                        {t('VideoUpload.UploadCompleted')}
                    </p>
                    <p className="text-sm text-gray-500">
                        {t('VideoUpload.VideoBeingProcessed')}
                    </p>
                </div>
            )}

            {uploadState.status === 'error' && (
                <div className="text-center py-8">
                    <div className="text-red-500 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                    <p className="text-lg font-medium text-red-600 mb-2">
                        {t('VideoUpload.UploadFailed')}
                    </p>
                    <p className="text-sm text-gray-500 mb-4">
                        {uploadState.errorMessage}
                    </p>
                    <button
                        onClick={() => {
                            setUploadState(prev => ({ 
                                ...prev, 
                                status: 'idle',
                                errorMessage: null 
                            }));
                            setStartTime(null);
                            setUploadedBytes(0);
                        }}
                        className="bg-black text-white px-4 py-2 rounded-md text-sm hover:bg-gray-800"
                    >
                        {t('VideoUpload.TryAgain')}
                    </button>
                </div>
            )}
        </div>
    );
});

export default ChunkedUploader;